import { PageShell } from "../components/layout/PageShell";
import { CtaBand } from "../components/layout/CtaBand";

const PriceRow = ({ name, detail, price, testId }) => (
  <div className="flex items-start justify-between gap-6 py-5 border-b border-[#E7E2D8] last:border-0" data-testid={testId}>
    <div>
      <p className="font-medium text-[#292A26]">{name}</p>
      {detail && <p className="mt-1 text-sm text-[#575953] leading-relaxed">{detail}</p>}
    </div>
    <p className="font-serif text-xl text-[#3F4A38] whitespace-nowrap">{price}</p>
  </div>
);

const Card = ({ title, eyebrow, children, testId }) => (
  <section className="py-8" data-testid={testId}>
    <div className="max-w-4xl mx-auto px-6 sm:px-8">
      <div className="bg-white rounded-3xl border border-[#DDD7CB] p-7 sm:p-10">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="font-serif text-2xl sm:text-3xl font-medium text-[#292A26] tracking-tight mt-2">{title}</h2>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  </section>
);

export default function PricesPage() {
  return (
    <PageShell title="Prices">
      <section className="pt-20 pb-8 sm:pt-28 sm:pb-10" data-testid="prices-hero">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <p className="eyebrow">Prices</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-[#292A26] tracking-tight mt-4" data-testid="prices-heading">
            Clear and simple.
          </h1>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#575953] max-w-2xl">
            Straightforward pricing with everything important included. If your situation
            is a little different, such as multiple animals, unusual routines or a home further from
            Inverness. Carly will agree a tailored quote before anything is booked.
          </p>
        </div>
      </section>

      <Card eyebrow="Dog Walking" title="Regular walks · Mon, Tue & Fri" testId="prices-walking">
        <PriceRow
          testId="price-group-walk"
          name="Group walk"
          detail="One hour, with collection and drop-off included. Carefully matched company at Mary's Meadow and other chosen locations."
          price="£18"
        />
        <PriceRow
          testId="price-solo-walk"
          name="Solo walk"
          detail="One hour, one-to-one. For dogs who benefit from individual attention or a quieter environment."
          price="£22"
        />
        <p className="mt-4 text-sm text-[#575953]">
          Two dogs from the same household are welcome. Ask Carly for arrangements and pricing when you enquire.
        </p>
      </Card>

      <Card eyebrow="In-home Pet Sitting" title="While you're away" testId="prices-sitting">
        <PriceRow
          testId="price-silver"
          name="Silver"
          detail="A 12-hour overnight stay in your home, keeping your pet's evening and morning routine intact. Arrival and departure times agreed when you book."
          price="£65 / night"
        />
        <PriceRow
          testId="price-gold"
          name="Gold"
          detail="The premium service: a 24-hour in-home care period with a minimum of three walks a day, feeding, company and updates. Carly may be away for up to two hours. The routine and absence arrangements are agreed before booking."
          price="£85 / 24 hrs"
        />
        <p className="mt-4 text-sm text-[#575953]">
          Additional animals, travel beyond Inverness or extended arrival and departure times may affect the quote, and everything is always agreed in advance.
        </p>
      </Card>

      <Card eyebrow="Home Visits" title="Cats, puppies, seniors & more" testId="prices-visits">
        <PriceRow
          testId="price-visit-lengths"
          name="Pop-in and home visits"
          detail="15-minute pop-ins and 30 or 60-minute visits, with multi-visit arrangements for days away."
          price="On enquiry"
        />
        <p className="mt-4 text-sm text-[#575953]">
          Visit pricing is confirmed when you enquire, so it reflects your pet, your location and the routine they need.
        </p>
      </Card>

      <section className="py-8 pb-4" data-testid="prices-policies">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div className="rounded-3xl bg-[#E7E2D8]/60 border border-[#DDD7CB] p-7 sm:p-9">
            <h2 className="font-serif text-2xl font-medium text-[#292A26]">Payment &amp; cancellations.</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#575953]">
              Current payment terms, notice periods and cancellation arrangements are
              shared and agreed when your booking is confirmed, so the terms you're
              given are always the current ones.
            </p>
          </div>
        </div>
      </section>

      <CtaBand
        testId="prices-cta"
        heading="Not sure which service fits?"
        copy="Tell Carly a little about your pet and what you need and she'll point you in the right direction."
      />
    </PageShell>
  );
}
